import { useEffect, useRef } from 'react'

const CENTRE = 16
const TILE = 512
const COLUMNS = 6
const INTERACTIVE = '(min-width: 851px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)'

/** A decoded atlas keeps pointer tracking independent of video seeking/network requests. */
export default function EaglePerch({ hidden }: { hidden: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || hidden) return
    const context = canvas.getContext('2d')
    if (!context) return

    const media = window.matchMedia(INTERACTIVE)
    let disposeTracking = () => {}
    const configure = () => {
      disposeTracking()
      canvas.style.opacity = '0'
      if (!media.matches) return

      const atlas = new Image()
      let disposed = false
      let ready = false
      let visible = false
      let current = CENTRE
      let target = CENTRE
      let drawn = -1
      let animation = 0
      let previousTime = 0
      let bounds = canvas.getBoundingClientRect()

      const draw = () => {
        const frame = Math.round(current)
        if (frame === drawn) return
        context.drawImage(atlas, (frame % COLUMNS) * TILE,
          Math.floor(frame / COLUMNS) * TILE, TILE, TILE, 0, 0, TILE, TILE)
        drawn = frame
      }
      const tick = (time: number) => {
        animation = 0
        if (!ready || !visible || document.hidden) return
        const elapsed = previousTime ? Math.min(time - previousTime, 50) : 16
        previousTime = time
        // Limit steps as well as easing so a fast cross-screen move still reads as a turn.
        const step = (target - current) * (1 - Math.exp(-elapsed / 85))
        current += Math.max(-elapsed / 22, Math.min(elapsed / 22, step))
        if (Math.abs(target - current) < 0.04) current = target
        draw()
        if (current !== target) animation = requestAnimationFrame(tick)
      }
      const start = () => {
        if (ready && visible && !document.hidden && !animation) {
          previousTime = 0
          animation = requestAnimationFrame(tick)
        }
      }
      const reset = () => { target = CENTRE; start() }
      const move = (event: PointerEvent) => {
        if (event.pointerType !== 'mouse' || !visible) return
        // The head sits left of the body's centre within the fixed source crop.
        const headX = bounds.left + bounds.width * 0.445
        const distance = event.clientX - headX
        const deadZone = 18
        const reach = Math.max(140, bounds.width * 0.65)
        const amount = Math.min(1, Math.max(0, Math.abs(distance) - deadZone) / reach)
        target = CENTRE + Math.sign(distance) * Math.round(amount * CENTRE)
        start()
      }
      const measure = () => { bounds = canvas.getBoundingClientRect() }
      const visibility = () => {
        if (document.hidden) {
          cancelAnimationFrame(animation)
          animation = 0
          target = current = CENTRE
          if (ready) draw()
        } else { measure(); start() }
      }
      const observer = new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting
        measure()
        if (visible) start()
        else {
          cancelAnimationFrame(animation)
          animation = 0
          target = current = CENTRE
          if (ready) draw()
        }
      })
      observer.observe(canvas)
      window.addEventListener('pointermove', move, { passive: true })
      window.addEventListener('resize', measure, { passive: true })
      window.addEventListener('scroll', measure, { passive: true })
      window.addEventListener('blur', reset)
      document.documentElement.addEventListener('pointerleave', reset)
      document.addEventListener('visibilitychange', visibility)
      atlas.src = '/media/eagle/perch-sprite.webp'
      void atlas.decode().then(() => {
        if (disposed) return
        ready = true
        draw()
        canvas.style.opacity = '1'
        start()
      }).catch(() => { /* The static portrait stays visible if the atlas cannot load. */ })

      disposeTracking = () => {
        disposed = true
        cancelAnimationFrame(animation)
        observer.disconnect()
        window.removeEventListener('pointermove', move)
        window.removeEventListener('resize', measure)
        window.removeEventListener('scroll', measure)
        window.removeEventListener('blur', reset)
        document.documentElement.removeEventListener('pointerleave', reset)
        document.removeEventListener('visibilitychange', visibility)
      }
    }
    configure()
    media.addEventListener('change', configure)
    return () => {
      disposeTracking()
      canvas.style.opacity = '0'
      media.removeEventListener('change', configure)
    }
  }, [hidden])

  return <div className="eagle-portrait" aria-hidden={hidden}>
    <img src="/media/eagle/perch-static.webp" width={512} height={512}
      alt="Illustrated white-breasted sea eagle perched on a branch, with a white head and slate-grey wings"
      fetchPriority="high" draggable={false} />
    <canvas ref={canvasRef} className="eagle-perch-sprite" width={TILE} height={TILE} aria-hidden="true" />
  </div>
}
